// Mirrors schemas/*.schema.json. Keep in sync; `npm run content -- validate` is the truth.

export type Color = string;
export type Point = [number, number];

export type Shape =
  | { t: 'rect'; x: number; y: number; w: number; h: number; rx?: number; fill?: Color; stroke?: Color; lw?: number }
  | { t: 'circle'; x: number; y: number; r: number; fill?: Color; stroke?: Color; lw?: number }
  | { t: 'poly'; pts: Point[]; fill?: Color; stroke?: Color; lw?: number }
  | { t: 'line'; pts: Point[]; stroke: Color; lw?: number };

export interface Sprite {
  size: [number, number];
  shapes: Shape[];
}

export type ZoneName = 'downtown' | 'suburb' | 'industrial' | 'rural' | 'park' | 'water';
export type ZoneWeights = Partial<Record<ZoneName, number>>;

export interface Terrain {
  id: string;
  name: string;
  color: Color;
  color2?: Color;
  walkable: boolean;
  drivable: boolean;
  boatable?: boolean;
  buildable: boolean;
  speedMul: number;
  decor?: { density: number; sprite: Sprite; height?: number }[];
  tags?: string[];
  notes?: string;
}

export type RoadClass = 'highway' | 'avenue' | 'street' | 'lane' | 'dirt' | 'rail';

export interface Road {
  id: string;
  name: string;
  class: RoadClass;
  width: number;
  color: Color;
  edgeColor?: Color;
  sidewalk?: number;
  sidewalkColor?: Color;
  markings?: { center?: Color; dashed?: boolean; lanes?: number; /** (owner, Sep 13) metres between manhole covers, drawn on the lanes; absent = none */ manholes?: number };
  /** km/h; the traffic cameras (game/cameras.ts) fine above it (default 50) */
  limit?: number;
  speedMul: number;
  zones: ZoneWeights;
  elevated?: boolean;
  tags?: string[];
  notes?: string;
}

export interface Building {
  id: string;
  name: string;
  footprint: [number, number];
  stories: number;
  storyHeight?: number;
  roof: Sprite;
  /** (owner, Sep 23) the roof as a solid: gable, hip, pyramid, shed, gambrel, mansard, saltbox (world/roofs.ts); absent or flat = the flat roof sprite */
  roofForm?: { type: 'flat' | 'gable' | 'hip' | 'pyramid' | 'shed' | 'gambrel' | 'mansard' | 'saltbox'; pitch?: number; color?: Color };
  wall: { color: Color; windowColor?: Color; windowsPerStory?: number; windowSpacing?: number };
  solid?: boolean;
  rotatable?: boolean;
  /** interior: 'none' = sealed; or a kit override for the generated floor plan (see docs/DESIGN_INTERIORS.md) */
  interior?: 'none' | { kit?: 'house' | 'apartment' | 'office' | 'shop' | 'hall' | 'open' };
  /** purchasable: default for residential kits (hashed for-sale chance); false opts out; object sets price/chance/slots */
  /** the chain it belongs to (content/brands): the sign shows the brand instead of OPEN/EATS */
  brand?: string;
  /** (owner, Sep 22) other words the map's search finds this building by ("second hand", "thrift shop" for a pawn shop) */
  search?: string[];
  property?: false | { price?: number; chance?: number; slots?: number; /** (owner, Sep 22) rented: $ a game day, and the hashed share of these buildings on the rental market (default a quarter) */ rent?: number; rentChance?: number };
  zones: ZoneWeights;
  rarity?: number;
  setback?: number;
  minLotDepth?: number;
  /** (owner, Sep 29) a fire escape on a side or back wall where the lot has room: true always tries, false never; absent, a building of 3–10 storeys with an interior tries */
  fireEscape?: boolean;
  ground?: string;
  tags: string[];
  /** how many everyday snacks/drinks a store of some particular kind keeps by the till (0-4; default: 0-2, hashed per building) */
  snacks?: number;
  notes?: string;
}

export type HeldKind = 'pistol' | 'smg' | 'rifle' | 'shotgun' | 'sniper' | 'lmg' | 'launcher' | 'flame' | 'thrown' | 'bat' | 'knife' | 'machete' | 'axe' | 'crowbar' | 'chainsaw';

export type WeaponClass = 'melee' | 'pistol' | 'smg' | 'shotgun' | 'rifle' | 'sniper' | 'lmg' | 'launcher' | 'thrown' | 'mounted' | 'flame';

export interface Weapon {
  id: string;
  name: string;
  class: WeaponClass;
  damage: number;
  fireRate: number;
  auto: boolean;
  magazine: number;
  reloadTime: number;
  range: number;
  spread: number;
  pellets?: number;
  projectileSpeed: number;
  projectile?: 'bullet' | 'shell' | 'rocket' | 'grenade' | 'flame' | 'none';
  splash?: number;
  /** air: the shot picks the nearest aircraft in front as it is fired and follows it (game.ts fireWeapon / updateProjectiles) */
  homing?: 'air';
  ammo: string;
  /** (owner, Oct 2) a gun held in one hand (1) fires from a vehicle — any way from an open one, out of the side windows of a closed one with them down; 2 never (game/driveby.ts). Absent: a pistol 1, the rest 2 */
  hands?: 1 | 2;
  tracer?: Color;
  /** (owner, Sep 28) how it is drawn in the hands (engine/held.ts): absent, the class's own shape — melee a bat */
  held?: HeldKind;
  /** (owner, Sep 30: explosives) what it becomes thrown or set down (game/charges.ts) */
  /** (Sep 30) fired through a suppressor (set on the fired copy by game/attachments.ts effective — never in content) */
  quiet?: boolean;
  charge?: { fuse?: number; stick?: boolean; place?: boolean; remote?: boolean; trap?: boolean; effect?: 'smoke' | 'flash'; radius?: number; duration?: number };
  icon: Sprite;
  /** (owner, Sep 13; retuned Sep 25) damage multiplier against a tree — a chainsaw 0.75 (a fast swing), an axe 0.8; absent: a swing a fifth, a shot a twentieth */
  tree?: number;
  /** kg on the back; by class when absent */
  weight?: number;
  /** (Sep 25) a melee swing's stamina as a share of a plain swing's (STAMINA.swing): the chainsaw's motor does the work, 0.4; absent 1 */
  stamina?: number;
  tier: number;
  tags: string[];
  notes?: string;
}

/** (owner, Sep 25) `drone`: a remote-controlled flyer launched from an item (`Item.flies`) — nobody rides it (src/game/drone.ts) */
export type VehicleClass = 'car' | 'sports' | 'truck' | 'bus' | 'bike' | 'boat' | 'plane' | 'helicopter' | 'tank' | 'train' | 'trailer' | 'drone';

export interface VehicleFront { sprite: Sprite; windshield: { x: number; y: number; w: number; h: number }; plate?: { x: number; y: number; w: number; h: number }; lights?: { x: number; y: number; r: number }[]; driver?: 'left' | 'right' | 'centre' }
export interface Vehicle {
  id: string;
  name: string;
  class: VehicleClass;
  /** (owner, Sep 22) pieces of a place's stuff it carries (game/stuff.ts; default 12 for a cargo truck, 6 for a cargo car, none otherwise) */
  stuff?: number;
  size: [number, number];
  /** the body's height along its length, front first: [start, end, metres] — and (Oct 1) optionally [.., inset, below]: the part above
   *  `below` stands `inset` metres in from each long side (a glasshouse narrower than the doors) — src/engine/vehicle-height.ts */
  height?: import('../engine/vehicle-height').HeightProfile;
  /** (Oct 1) where the flashing emergency/service lights go: `y` metres back from the nose to the bar's middle, `w` its width (default 55 % of the width); absent, 12 % of the length ahead of the middle */
  lightbar?: { y: number; w?: number };
  /** (owner, Oct 1) `outline`: the long sides stand on the top sprite's outline (a boat's narrowing bow, a bike narrower than its footprint), not on the footprint's edges */
  walls?: 'outline';
  sprite: Sprite;
  stats: { maxSpeed: number; reverseSpeed?: number; accel: number; brake: number; turnRate: number; grip?: number; mass: number; hp: number; /** minutes of driving at cruise on a full tank */ range?: number };
  power?: 'gas' | 'electric';
  /** the vehicle head-on, for the traffic cameras' evidence picture (draw/evidence.ts): metres, the driver shows through `windshield` */
  front?: VehicleFront;
  /** (owner, Sep 13) a logging vehicle's load: how many logs it carries (0 with the `harvester` tag: the machine that fells) */
  logs?: number;
  /** (owner, Sep 25) a harvester's head: hp a second it takes off a tree while F is held (a tree is forty a metre); absent: 120 */
  fell?: number;
  /** (owner, Sep 16) a garbage truck's load: how many bin bags it carries (game.ts stepGarbage) */
  bags?: number;
  /** (owner, Sep 13) the body seen from the right, front at x = 0, size [length, tallest height] — skewed onto the visible long face; tools/gen-vehicle-sides.mjs */
  side?: Sprite;
  /** the tail seen from behind, size [width, back height] */
  back?: Sprite;
  sides?: 'generated' | 'hand';
  flight?: { maxAltitude: number; climbRate: number; stallSpeed: number };
  /** what it sounds like (src/game/engine-sound.ts picks a default from class, tags and mass) */
  sound?: { engine?: 'inline4' | 'v6' | 'v8' | 'diesel' | 'electric' | 'bike' | 'twostroke' | 'outboard' | 'prop' | 'turbine' | 'rotor' | 'rail'; pitch?: number; gears?: number };
  /** (owner, Oct 2) the riders sit in the open (a motorbike, a convertible, an open boat): a one-handed gun fires any way from it (game/driveby.ts). Absent: true for class bike */
  open?: boolean;
  seats: number;
  weapons?: string[];
  zones: ZoneWeights;
  rarity?: number;
  /** $ at a dealer (default: from class and top speed) */
  price?: number;
  tags: string[];
  notes?: string;
}

export interface Biome {
  id: string;
  name: string;
  weight: number;
  rules: { terrain: string; elevMin?: number; elevMax?: number; moistMin?: number; moistMax?: number }[];
  /** render tints: a translucent roof cap (snow) and a scene colour cast */
  look?: { roofCap?: string; roofCapAlpha?: number; cast?: string; castAlpha?: number; /** what falls when it rains here: snow drifts instead of streaks; a dry biome halves the rain */ precipitation?: 'rain' | 'snow' | 'dry' };
  tags?: string[];
  notes?: string;
}

export interface Zone {
  id: ZoneName;
  name: string;
  densityMin: number;
  blockSize: number;
  roadChance: number;
  lotDepth: number;
  lotWidthMin: number;
  lotWidthMax: number;
  coverage: number;
  groundTerrain?: string;
  tags?: string[];
  notes?: string;
}

export interface Place {
  id: string;
  name: string;
  x: number; y: number; w: number; h: number;
  /** (owner, Sep 14) tiles between copies: the place repeats on that grid, one a cell, wherever the ground allows — x,y are then ignored */
  every?: number;
  /** (perf, Sep 17) on a copy of a repeating place: the grid cell it is the copy for (`PlaceIndex` keys), so a main thread can learn it from a worker's chunk */
  cell?: string;
  /** the part that has to be open flat ground for a copy to stand (a runway); the whole rect when absent */
  clear?: { x: number; y: number; w: number; h: number };
  /** (owner, Sep 16) with `every` equal to the settlement grid (1024): one copy a town, just outside its rim on open ground in a hashed direction; cells without a town keep none */
  atTown?: boolean;
  seed?: number;
  zone?: ZoneName;
  terrain?: string;
  roads?: 'keep' | 'none' | 'major';
  lots?: 'keep' | 'none';
  buildings?: { id: string; x: number; y: number; rot?: 0 | 1 | 2 | 3 }[];
  spawns?: { vehicle: string; x: number; y: number; angle?: number; /** (owner, Sep 25: Area 69's jeep) it drives this loop of relative points, round and round, until somebody takes it */ route?: [number, number][]; /** (owner, Sep 25: "hard even with 4 online players") manned once the place's `garrison` alarm is up: turns on the nearest raider and fires this weapon, `damage` a hit, `rate` shots a second */ crew?: { weapon: string; damage: number; rate: number }; /** (owner, Sep 25: no escaping in the saucer) nobody gets in while the place's garrison alarm is up */ lockdown?: boolean }[];
  paint?: { terrain: string; x: number; y: number; w: number; h: number }[];
  /** (owner, Sep 25: "easter egg: very rare place: area 69 … surrounded by no roads … not near any cities") with `every`: the copy
   *  stands on the most remote open ground of its cell (world/places.ts `remoteSpot`) — in one of these biomes, its edge `townGap`
   *  tiles past every town's rim, no road or railway within `roadGap` tiles of its rect; a cell with no such spot keeps none */
  remote?: { biomes?: string[]; townGap: number; roadGap: number; /** (owner: "very rare") the share of the grid's cells that get one at all, by the cell's hash (default 1) */ share?: number };
  /** (owner, Sep 25: Area 69) the ground inside is levelled to the rect's mean height, feathered over this many tiles in from its edge */
  level?: number;
  /** (owner, Sep 25) props the place sets down itself (relative coords, the tile the sprite is centred on) */
  props?: { id: string; x: number; y: number }[];
  /** (owner, Sep 25: "heavily guarded") soldiers posted at a spot, or walking a route of relative points (game/sentries.ts) */
  guards?: { x: number; y: number; weapon: string; hp?: number; /** a hit's damage (default 8) and shots a second (default 1.4) */ damage?: number; rate?: number; route?: [number, number][]; /** goes out through the gate after a trespasser who ran (the rest hold the fence) */ chase?: boolean }[];
  /** (owner, Sep 25) a fenced rect (relative) the guards defend: warned `warn` tiles out, shot at inside, `wanted` stars for going in */
  restricted?: { x: number; y: number; w: number; h: number; warn: number; wanted: number; sign: string; enter: string; /** the gate: its middle on the fence line (relative), the way out and in for a soldier who chases */ gate?: [number, number] };
  /**
   * (owner, Sep 25: "danger should be something that should be hard even with 4 online players co-oping") the guards' alarm: raised
   * by a trespasser or a shot soldier, held `alarm` seconds after the last raider was seen within `reach` tiles; while it is up every
   * player in reach is a target and held at `wanted` stars, the crewed vehicles man their guns, and every `every` seconds a wave of
   * `size` soldiers comes out of the `from` doors (the first at once), `squad` loadouts in turn, up to `max` alive — a fixed strength
   * (owner: "the hardness should be static"), the same for one raider or four
   */
  garrison?: { alarm: number; reach: number; wanted: number; every: number; size: number; max: number; from: [number, number][]; squad: { weapon: string; hp: number; damage: number; rate: number }[] };
  /** (owner, Sep 25) something to find inside: items (and cash) lying at a spot each time the place is visited */
  loot?: { x: number; y: number; items: Record<string, number> }[];
  tags?: string[];
  notes?: string;
}

export interface Prop {
  id: string;
  name: string;
  sprite: Sprite;
  height: number;
  /** (owner, Sep 28) people sit on it (F beside it; walkers rest on it), facing south */
  seat?: boolean;
  /** `place`: never scattered — only a place's own `props` list sets it down (owner, Sep 25: Area 69's saucer bits) */
  placement: 'sidewalk' | 'lot' | 'park' | 'roadside' | 'indoor' | 'shore' | 'place' | 'board';
  /** (owner, Sep 25: "bigger billboards that could have logos or images") placement `board`: a big billboard — the face lifted on posts (the sprite is one post, seen from above) */
  board?: BoardSpec;
  /** indoor furniture: which interior kits, and where in the room */
  rooms?: ('house' | 'apartment' | 'office' | 'shop' | 'hall' | 'open')[];
  indoor?: 'wall' | 'back' | 'front' | 'anywhere';
  zones: ZoneWeights;
  density: number;
  /** vending: item ids sold at their value */
  sells?: string[];
  /** (owner, Sep 22) what the piece costs in the stores (game/stuff.ts): the crate sells it back for half; indoor props without one are $200 */
  price?: number;
  /** (owner, Sep 25) a TV (tag `tv`): the screen's rectangle [x, y, w, h] in sprite units, lit while somebody watches */
  screen?: [number, number, number, number];
  tags?: string[];
  notes?: string;
}

/** (owner, Sep 25) a big billboard's shape (content/props, placement `board`; world/generator.ts places it, engine/billboards.ts draws it) */
export interface BoardSpec {
  /** road = beside a road of `roads` on free ground, on posts; roof = on a flat roof downtown */
  on: 'road' | 'roof';
  /** the face: width along the road and depth (tiles), and how high it stands (metres; on a roof, above the roof) */
  w: number; d: number; lift: number;
  /** road: the road classes it stands beside, and one spot per this many tiles along them (density × the zone weight is the chance a spot gets one) */
  roads?: RoadClass[]; every?: number;
  /** roof: the building must be at least this tall (metres) and this long (tiles) */
  minHeight?: number; minLength?: number;
  /** the posts and the frame round the face */
  post?: Color; frame?: Color;
  /** (Sep 25 follow-up) a screen: a new ad every few seconds (the world's `adCycleSeconds`), the same for everyone at the same moment */
  digital?: boolean;
}

/** (owner, Sep 30) a gun attachment: its slot on the weapon, which classes it fits, what it changes */
export type AttachSlot = 'optic' | 'muzzle' | 'laser' | 'grip';
export interface AttachSpec { slot: AttachSlot; fits?: ('pistol' | 'smg' | 'rifle' | 'shotgun' | 'sniper' | 'lmg')[]; spread?: number; moveSpread?: number; kick?: number; damage?: number; still?: boolean; quiet?: boolean; laser?: boolean; dot?: boolean; lead?: number }
export interface Item {
  id: string;
  name: string;
  kind: 'cash' | 'food' | 'gear' | 'valuable' | 'key';
  /** whose it is (content/brands) */
  brand?: string;
  /** which animals will eat it (content/animals kinds) — anything else cannot be fed to them (owner: no chocolate for dogs) */
  feeds?: ('cat' | 'dog')[];
  /** store price ($); selling gives 50 % */
  value: number;
  /** kg */
  weight?: number;
  restore?: { food?: number; water?: number; hp?: number };
  /** one line for the player: what it does and how you set it off (the Backpack card, and the answer when there is nothing to press) */
  use?: string;
  sprite: Sprite;
  loot?: { chance?: number; zones?: ZoneWeights; /** chance (0..1) of one lying about inside a building the first time somebody walks in that day, by interior kit (house, shop, apartment, office, hall, open); a bare number applies to every kit */ indoor?: number | Partial<Record<'house' | 'shop' | 'apartment' | 'office' | 'hall' | 'open', number>> };
  shops?: string[];
  /** (owner, Sep 25) a remote-controlled aircraft: the vehicle (class `drone`) it takes off as when used (src/game/drone.ts) */
  flies?: string;
  /** (owner, Sep 28) a trophy (an achievement's reward): never dropped, sold, stashed, stolen or lost, and weightless in the backpack (game/achievements.ts) */
  forever?: boolean;
  /** (owner, Sep 30) body armour worn from the pockets (the best of each slot) */
  armor?: { slot: 'vest' | 'helmet'; level: 1 | 2 | 3; reduce: number; hp: number };
  /** (owner, Sep 30) a gas can, full (`share`, `empty`) or empty (`full`, `price`) */
  fuelCan?: { share?: number; empty?: string; full?: string; price?: number };
  /** (owner, Sep 30) what using it does (game/gadgets.ts), and a placed one's battery (seconds) */
  gadget?: 'night_vision' | 'binoculars' | 'spy_camera' | 'motion_sensor'; battery?: number;
  /** (owner, Sep 30) a gun attachment (game/attachments.ts) */
  attach?: AttachSpec;
  tags: string[];
  notes?: string;
}

/** A brand of this world (content/brands): a name and a slogan that wink at a real one. Shops carry it on the sign, items in their name, billboards and the radio in their ads. */
export interface Brand {
  id: string;
  name: string;
  slogan: string;
  /** what it is: burgers, coffee, soda, cars, phones, clothes, fuel, bank, insurance, furniture, delivery, streaming, beer, energy_drink, sportswear, supermarket, convenience, pharmacy, gym, taxi, electronics, tuning, pawn, bakery, noodles, marina, lounge, scrap */
  category: string;
  color: string;
  /** a second colour for the sign */
  color2?: string;
  /** shown on roadside billboards (default true) */
  billboard?: boolean;
  /** (owner, Sep 25: "billboards that could have logos or images") the brand's mark: vector shapes in a square (usually 1 × 1), on its billboards beside the name */
  logo?: Sprite;
  /** a content/media picture (show `billboard`) the big boards show instead of the name and the logo, once the owner rendered and picked it */
  image?: string;
  /** extra ad lines the city station reads (the `ad` kind carries the paid jingles) */
  lines?: string[];
  tags: string[];
  notes?: string;
}
/** Lines the city station reads between songs (content/radio): news, talk, weather, traffic, idents, jingles. Placeholders: {town} {name} {brand} {slogan} {day} {hour} {weather} {next} */
/** Who reads a segment: the browser's voice is picked to match (sex, pitch, rate); a pre-rendered file may replace it later. */
export interface Speaker { name: string; sex: 'f' | 'm'; mood: 'calm' | 'cheery' | 'urgent' | 'sleepy' | 'serious' | 'salesy' | 'gruff'; pitch: number; rate: number }
export interface RadioLines {
  id: string;
  name: string;
  kind: 'news' | 'talk' | 'weather' | 'traffic' | 'ident' | 'ad_read' | 'headline' | 'intro' | 'song_intro' | 'back_announce' | 'bit';
  /** who reads these (default: the station's presenter for the kind) */
  speaker?: Speaker;
  lines: string[];
  /** (Sep 25) headlines: the happening without its names (only {town}) — rendered per town, read in place of a headline whose own words have no file */
  generic?: string[];
  /** the station these lines belong to (content/stations); none = syndicated to every station */
  station?: string;
  weight?: number;
  night?: boolean;
  tags: string[];
  notes?: string;
}
/** Lines people on the street say to themselves, to each other or into a phone as you pass (content/chatter); spoken by the browser's voices and shown in a bubble. */
export interface Chatter {
  id: string;
  name: string;
  /** who says it: a walker to nobody in particular, one walker to another, or on the phone */
  mode: 'self' | 'other' | 'phone';
  /** lines; `{name}` becomes a random first name, `{town}` the town */
  lines: string[];
  /** relative pick weight (default 1) */
  weight?: number;
  /** only at night (hour 21–5), only in these zones */
  night?: boolean;
  zones?: string[];
  /** only while the player's karma is within [min, max] (docs/DESIGN_KARMA.md: the street greets a saint, shuns a villain) */
  karma?: { min?: number; max?: number };
  /** only when the player is within this many metres (a remark aimed at you) */
  near?: number;
  /** a reaction: said by the person it happens to, when it happens (carjack: the driver pulled out of the car) — never in the idle rotation */
  on?: 'carjack' | 'tipped' | 'gunshot' | 'explosion' | 'crash' | 'honked' | 'flashbanged' | 'aimed_at' | 'wanted' | 'siren' | 'hurt' | 'dancing' | 'near_miss'; /** (Oct 2) idle lines only in the rain, or only in the dry */ weather?: 'rain' | 'dry';
  tags: string[];
  notes?: string;
}
/** A cat or a dog that lives on the streets (content/animals): looks, size, speed, where it turns up. */
export interface Animal {
  id: string;
  name: string;
  kind: 'cat' | 'dog';
  /** body colour and a second colour (patches, ears) */
  color: Color; color2?: Color;
  /** tiles: body radius */
  size: number;
  /** m/s when it trots */
  speed: number;
  zones: ZoneWeights;
  rarity?: number;
  tags: string[];
  notes?: string;
}

/**
 * Something you can buy to change how an owned thing looks: your avatar (skin tone, top, hat),
 * your car (paint) or your house (the nameplate). Bought once, kept for good, switched for free.
 */
/** (owner, Sep 22) a web store on the phone's browser, Surf (docs/DESIGN_STUFF.md): the things of a place, delivered to any place you own or rent */
export interface Store { id: string; name: string; tagline?: string; url?: string; color?: string; /** an HTML page for the store: the folder under content/sites (docs/DESIGN_STUFF.md "HTML sites"); without one Surf draws the shelf itself */ site?: string; /** the buildings (tags) a courier collects the store's orders from — the furniture run job (game/jobs.ts) */ warehouse?: string[]; /** prop ids */ /** (owner, Sep 22) a listings site instead of a shelf: 'property' = the houses, flats and plots for sale or rent around the player (game/listings.ts) */ lists?: 'property'; sells: string[]; /** the shelf price is the prop's price × this (default 1) */ markup?: number; delivery: { fee: number; /** real minutes */ minutes: number }; tags: string[]; notes?: string }
export interface Cosmetic {
  id: string;
  name: string;
  /** what it dresses: skin/top/hat/hair/beard/glasses/body = the avatar (docs/DESIGN_CHARACTER.md), paint = the owned car, plate = the house sign, facade = the house's walls and roof */
  slot: 'skin' | 'top' | 'hat' | 'hair' | 'beard' | 'glasses' | 'body' | 'paint' | 'plate' | 'facade';
  /** $ (0 = free, the defaults) */
  price: number;
  /** the colour it paints with */
  color: Color;
  /** hat shape (hat slot) or plate lettering colour (plate slot) */
  style?: string;
  tags: string[];
  notes?: string;
}

export interface Defence {
  id: string;
  name: string;
  /** garage: a roof over the driveway — the car in it is safe, and the house replaces or fetches it for free */
  /** (owner, Sep 16) mechanic: staff at a repair shop you own — mends your cars in the yard, `earns` a day into the till */
  kind: 'turret' | 'camera' | 'door' | 'alarm' | 'guard' | 'garage' | 'mechanic';
  price: number;
  earns?: number;
  /** $ per game day */
  upkeep: number;
  weapon?: string;
  range?: number;
  hp?: number;
  sprite: Sprite;
  tags: string[];
  notes?: string;
}

export interface Job {
  id: string;
  name: string;
  kind: 'delivery' | 'taxi' | 'bounty' | 'race' | 'raid' | 'heist' | 'escort' | 'service' | 'route' | 'work' | 'crew' | /** (Sep 30) a mission (content/missions) running as a job */ 'mission';
  /** crew: the party members needed (src/game/crew.ts — roles: hands, driver, lookout) */
  party?: number;
  /** work (growing the world, world/overlay.ts): plan = city planner, pave = road crew, build = construction crew, plant = parks crew; `pay.perTile` is the pay per plan / tile / unit / plant, from the city budget */
  work?: 'plan' | 'pave' | 'build' | 'plant' | 'radio' | 'mechanic' | 'police';
  /** (Sep 25) work police: the calls dispatch sends (src/game/patrol.ts) */
  dispatch?: { kind: 'speeder' | 'chase' | 'burglar' | 'crash'; weight: number; pay: number; say: string }[];
  /** (Sep 25) work police: the cosmetics worn on duty, over your own look */
  uniform?: string[];
  /** (owner, Sep 16) how far the pickup may be from you for an offer / an introduction (tiles; default 70) */
  reach?: number;
  /** service: the vehicle you get at the pickup (ambulance / fire_engine / tow_truck) — its tags decide the call: medical, fire, service */
  vehicle?: string;
  pay: { base: number; perTile: number };
  pickup: string[];
  dropoff: string[];
  minDist?: number;
  maxDist?: number;
  timeLimit?: number;
  /** race: junctions to pass on the way (default: one per 120 tiles) and the pace in tiles/s that sets the clock (default 12) */
  checkpoints?: number;
  pace?: number;
  text: string;
  zones: ZoneWeights;
  rarity?: number;
  /** offered only after this many finished jobs (a ladder: deliveries first, heists later) */
  unlock?: number;
  /** offered only while the player's karma is within [min, max] (docs/DESIGN_KARMA.md: Samaritan work above, the gangs' errands below) */
  karma?: { min?: number; max?: number };
  /** delivery: the item carried between the doors (default job_crate) */
  carry?: string;
  /** karma moved when the job is finished */
  karmaOnDone?: number;
  /** (C) media sent at the job's moments (content/media ids): a picture in the mail on accept, a message at the pickup, a clip when done… (docs/DESIGN_MEDIA.md) */
  media?: { offer?: string; accept?: string; pickup?: string; done?: string; fail?: string };
  /** besides the pay: 'plates' = stolen licence plates on your own car (cameras.ts) — a mission's reward, never sold */
  reward?: 'plates';
  /**
   * (owner, Sep 16) the job's introduction: with the player's "Job introductions" setting on, a job they have never done comes
   * to them once as a call or a message (`media`, a content/media entry — kind call for a voice/video call, show message for a
   * text) with a little story ("cover my shift, I'll pay you double"), and the job runs at double pay with `steps` on the phone.
   * Every job carries one, or says in `noIntro` why it does not (a story line, a party job, a karma-gated errand); lint asks.
   */
  intro?: { media: string; steps: string[] };
  noIntro?: string;
  tags: string[];
  notes?: string;
}

export interface Gang {
  id: string;
  name: string;
  /** jacket colour of the members */
  color: Color;
  /** members per group */
  size: [number, number];
  weapons: string[];
  /** building tags a group hangs out at */
  hangouts: string[];
  /** provoked: only when one of them is hurt; armed: also when you come close with a gun out; always: anyone within reach */
  hostility: 'provoked' | 'armed' | 'always';
  /** cash on each member */
  cash: [number, number];
  hp?: number;
  zones: ZoneWeights;
  rarity?: number;
  tags: string[];
  notes?: string;
}

/** Economy tunables: one file (content/economy/default.json); code falls back to ECONOMY_DEFAULTS field by field. */
export interface Economy {
  id: string;
  name: string;
  /** hold-ups: store take [min, max] ($), bank take [min, max]; big stores × */
  robbery: { store: [number, number]; storeBig: number; bank: [number, number]; seconds: number; bankSeconds: number; armedKeeperChance: number };
  /** businesses: $/day per tile of footprint × zone; price = income × days; rich kinds × */
  business: { perTile: number; priceDays: number; richMul: number; zone: Partial<Record<ZoneName, number>>; extortChance: number; extortShare: number };
  /** tickets: $ base + per tile */
  ticket: { base: number; perTile: number };
  /** the law: pay-off per star, arrest fine share and cap per star, bounty per star paid to whoever kills a wanted player */
  law: { payoffPerStar: number; fineShare: number; fineCapPerStar: number; bountyPerStar: number };
  /** tuning: engine stage prices as a share of the car, armour prices */
  tuning: { engine: number[]; armour: number[]; engineGain: number; armourGain: number; /** a full repair as a share of the car's price */ repair: number };
  /** what an NPC drops: base cash by zone × [0.3, 1.7] */
  loot: { zone: Partial<Record<ZoneName, number>> };
  /** dying: share of cash dropped */
  /** turf (a block held after a clear-out): $/day per business in it, days before a crew may try to take it back, that chance per day, days they get before the block is lost */
  turf: { tribute: number; retakeDays: number; retakeChance: number; graceDays: number };
  /** karma (−100…100): shop prices move by ±priceSpread at the ends (sell prices the other way); the police lose interest up to ±heatSpread faster/slower; strays come to (shun) you past ±animalAt */
  karma: { priceSpread: number; heatSpread: number; animalAt: number };
  deathDrop: number;
  /** $ for a full tank / a full charge at a station (0 = free) */
  fuel: { tank: number; charge: number };
  tags: string[];
  notes?: string;
}

export interface NameList {
  id: string;
  name: string;
  kind: 'town' | 'street' | 'business';
  weight?: number;
  prefixes?: string[];
  suffixes?: string[];
  whole?: string[];
  tags?: string[];
  notes?: string;
}

/** A radio ad (docs/DESIGN_RADIO.md): a synthesized jingle in one of the radio's styles, a ticker line, and what the sponsor pays per listener each time it plays. */
/** A station on the car dial (content/stations): the genres it plays and its own hosts — docs/DESIGN_MUSIC.md. */
export interface Station {
  id: string;
  name: string;
  tagline?: string;
  /** position on the dial, 1 = first; Community FM and the owned studios follow the last one */
  dial: number;
  /** song tags on its playlist */
  genres: string[];
  /** synthetic fallback mood and ad-bed style: 0 pop, 1 slow, 2 fast */
  style: 0 | 1 | 2;
  /** carries the news desk, weather and traffic (default true) */
  news?: boolean;
  tags: string[];
  notes?: string;
}
/** A track on Sprawl FM (content/songs): what an external music model renders it from — docs/DESIGN_MUSIC.md. */
export interface Song {
  id: string;
  /** the title on the ticker */
  name: string;
  artist?: string;
  /** song = the station's song slots; bed = a jingle bed under a commercial of that `style` */
  kind: 'song' | 'bed';
  style?: 0 | 1 | 2;
  /** style tags for the music model */
  prompt: string;
  /** structured lyrics; none = instrumental */
  lyrics?: string;
  seconds?: number;
  /** (Sep 13) the album (a media entry with show album — its picture is the cover), the decade, the moods and the tempo: what the Loops mixes sort by */
  album?: string; era?: SongEra; mood?: SongMood[]; bpm?: number;
  steps?: number;
  tags: string[];
  notes?: string;
}
export interface Ad {
  id: string;
  name: string;
  sponsor: string;
  /** the line shown on the HUD while it plays */
  text: string;
  /** the brand behind it (content/brands), for billboards and the city station */
  brand?: string;
  /** the spoken version the city station reads over the jingle (default: text) */
  read?: string;
  speaker?: Speaker;
  /** jingle style: 0 pop, 1 slow, 2 fast (the generated radio's styles) */
  style: 0 | 1 | 2;
  /** seconds on air */
  seconds: number;
  /** $ per listener per play */
  rate: number;
  rarity?: number;
  tags: string[];
  notes?: string;
}

/** (C) a story character who writes, sends pictures and calls the player (docs/DESIGN_MEDIA.md): the face and the voice */
export interface Character {
  id: string;
  name: string;
  sex: 'f' | 'm';
  role?: string;
  /** the image prompt for the portrait the owner renders and picks on the panel */
  portrait: string;
  /** the backstory: a few short paragraphs (the Characters page edits it, or the LLM writes it) */
  bio?: string;
  voice?: { ref?: string; /** a Chatterbox predefined voice by name, when there is no clip */ predefined?: string; mood?: Speaker['mood']; pitch?: number; rate?: number; exaggeration?: number; cfg_weight?: number };
  tags: string[];
  notes?: string;
}
/** (C) a picture, a clip or a call on the phone: the script it is rendered from, where it shows, who sends it */
export type SongEra = '50s' | '60s' | '70s' | '80s' | '90s' | '00s' | '10s';
export type SongMood = 'night' | 'warm' | 'upbeat' | 'dark' | 'mellow' | 'sad' | 'romantic' | 'driving';
/** (Sep 13) a mix in the Loops app (content/playlists): a rule resolved against the songs when the app opens (src/game/loops.ts mixSongs) */
export interface Playlist {
  id: string;
  name: string;
  category: 'mood' | 'genre' | 'era' | 'tempo' | 'time' | 'activity';
  description?: string;
  rules: { family?: string[]; plus?: string[]; era?: SongEra[]; mood?: SongMood[]; bpm?: [number, number]; hours?: [number, number]; /** songs without words (no lyrics, or tagged instrumental) */ instrumental?: boolean };
  tags: string[];
  notes?: string;
}
export interface Media {
  id: string;
  name: string;
  kind: 'image' | 'video' | 'call';
  /** feed: nothing is sent — a short clip in the phone's Tock app (owner, Sep 25; docs/DESIGN_CLIPS.md); tv: a clip on the houses' TVs (game/tv.ts) */
  show: 'mail' | 'message' | 'call' | 'album' | 'feed' | 'billboard' | 'tv';
  /** feed: the creator's handle ("@dj_overpass") */
  handle?: string;
  /** feed: the comments under the clip (Tock's 💬 sheet) */
  comments?: { by: string; text: string; likes?: number }[];
  /** (owner, Sep 25) a TV advert: the brand it sells (content/brands) — the placeholder card wears its colours */
  brand?: string;
  character?: string;
  /** the character is in the picture (their likeness goes into the prompt); off = they only send it */
  subject?: boolean;
  from?: string;
  text?: string;
  prompt?: string;
  line?: string;
  scene?: string;
  seconds?: number;
  size?: [number, number];
  seed?: number;
  /** (C, Sep 25) a video's tracks (docs/DESIGN_CLIPS.md): each renderable piece is a *part* with its own takes — see src/game/clips.ts */
  /** video: when the voice-over starts (seconds into the clip) */
  lineAt?: number;
  /** video: how the picture is made — the shots, the talking head, or both */
  layout?: ClipLayout;
  /** video: more shots after the first (`prompt`) */
  shots?: { prompt: string; seconds?: number; /** (Sep 27) the moment the shot ends on: a comic end keyframe the video moves to */ end?: string }[];
  /** (Sep 27) video: the first shot's end moment (a comic end keyframe the video moves to) */
  end?: string;
  /** video: the music — a song from the Music tab or a prompt rendered for this clip */
  music?: { song?: string; prompt?: string; lyrics?: string; seconds?: number; from?: number; at?: number; gain?: number };
  /** video: one-shot sounds on the timeline — a catalogue sound or a prompt rendered for this clip */
  sfx?: { id?: string; prompt?: string; seconds?: number; at: number; gain?: number }[];
  /** call: seconds it rings before an unanswered call counts as declined */
  ring?: number;
  /** call: the story's consequences of answering / declining (a job-offer call starts the job only when answered) */
  answer?: MediaEffects;
  decline?: MediaEffects;
  tags: string[];
  notes?: string;
}
export type ClipLayout = 'video' | 'head' | 'react' | 'split' | 'cut';
export interface MediaEffects { say?: string; karma?: number; fail?: boolean; send?: string }

/**
 * (B) A page of the phone's Help app — the game's own wiki (owner, Sep 9: "lets start building a wiki/Q&A type of
 * thing that goes through how things work"). Prose lives here; a `table` block is filled from the live numbers in the
 * code, so a page can quote the rules without going stale when they are tuned.
 */
export interface HelpPage {
  id: string;
  name: string;
  /** one line under the title in the index */
  blurb: string;
  icon: string;
  /** where it sits in the index (low first) */
  order?: number;
  body: HelpBlock[];
  tags?: string[];
  notes?: string;
}
export type HelpBlock =
  | { t: 'p'; text: string }
  | { t: 'h'; text: string }
  | { t: 'li'; text: string }
  | { t: 'q'; text: string; a: string }
  /** a table the game fills in: `karma+` and `karma-` are the two halves of the karma ledger */
  | { t: 'table'; of: 'karma+' | 'karma-' };

/**
 * (owner, Sep 28: "a huge improvement, add them") A kind of townsperson as seen from above (content/walkers): the cosmetics they pick
 * from (per person, hashed by id — game/npc-looks.ts), what they carry, their step. The street's walkers are these by weight.
 */
/** (owner, Sep 30) a scripted mission (content/missions; the engine src/game/missions.ts; the guide docs/DESIGN_MISSIONS.md) */
export type MissionSpot = 'site' | 'door' | 'back' | 'vantage' | 'roof' | 'roofEdge' | 'feFoot' | 'fireDoor' | 'drop' | 'crew';
export interface MissionGoal { type: 'have' | 'mounted' | 'at' | 'inside' | 'escape' | 'roof' | 'picked' | 'gadget' | 'watch' | 'drone' | 'bino' | 'nv' | 'smoke' | 'stunned' | 'down' | 'charge' | 'blast' | 'loot' | 'away' | 'wait' | 'all'; item?: string; weapon?: string; count?: number; spot?: MissionSpot; r?: number; vehicle?: boolean; floor?: number | 'top'; level?: number; door?: 'front' | 'fire'; kind?: 'cam' | 'sensor'; alt?: number; min?: number; seconds?: number }
export interface MissionStep { role?: string; say: string; media?: string; hold?: number; sync?: string; window?: number; late?: string; reveal?: boolean; alarmOk?: boolean; goal: MissionGoal }
export interface Mission {
  id: string; name: string; giver?: string; players?: [number, number]; roles?: { id: string; name: string; brief?: string }[];
  unlock?: { after?: string[]; jobs?: number }; offer: string; pay: number; timeLimit?: number;
  site: { tags: string[]; min?: number; max?: number; stories?: [number, number]; fireEscape?: boolean; drop?: string[]; dropMin?: number; dropMax?: number };
  setup?: { guards?: { at: MissionSpot; n?: number; weapon?: string; watch?: number; hp?: number; patrol?: MissionSpot[]; face?: MissionSpot }[]; loot?: { item: string; floor?: number | 'top'; at?: 'fireDoor' | 'inside' | 'back' }; lock?: 'front' };
  fail?: { alarm?: boolean; death?: boolean }; media?: { start?: string; done?: string; fail?: string };
  steps: MissionStep[]; tags: string[]; notes?: string;
}

export interface WalkerKind {
  id: string; name: string; weight: number;
  prop?: 'none' | 'backpack' | 'briefcase' | 'bag' | 'vest' | 'cane' | 'apron'; propColors?: Color[];
  tops?: string[]; hair?: string[]; hats?: string[]; bodies?: string[];
  hatChance?: number; glassesChance?: number; beardChance?: number;
  legColors?: Color[]; scale?: number; gait?: 'walk' | 'stroll' | 'jog';
  tags: string[]; notes?: string;
}

/**
 * (owner, Sep 28) An achievement (content/achievements; docs/DESIGN_ACHIEVEMENTS.md): a counter of the player's reaching `need` earns it
 * once, with its reward. The admin panel edits, switches off and adds them per world (server/achievements.ts).
 */
export interface Achievement {
  id: string;
  name: string;
  /** what to do, one line */
  description: string;
  /** an emoji: the card, the badge, the toast */
  icon: string;
  /** the counter it watches (game/achievements.ts COUNTERS, `jobs_<kind>`, `visit_<place>`) */
  counter: string;
  need: number;
  /** ??? in the app until earned */
  hidden?: boolean;
  /** where it sits in the app (low first; default 100) */
  order?: number;
  reward?: { /** an item id, given once — a `forever` one */ item?: string; /** a badge's title */ badge?: string; /** `game:<id>` / `app:<id>` */ unlock?: string; cash?: number };
  /** (a world's override only) switched off in this world */
  off?: boolean;
  tags: string[];
  notes?: string;
}

export interface ContentKinds {
  terrain: Terrain;
  road: Road;
  building: Building;
  weapon: Weapon;
  vehicle: Vehicle;
  biome: Biome;
  zone: Zone;
  place: Place;
  prop: Prop;
  namelist: NameList;
  item: Item;
  defence: Defence;
  job: Job;
  gang: Gang;
  economy: Economy;
  ad: Ad;
  cosmetic: Cosmetic;
  store: Store;
  animal: Animal;
  chatter: Chatter;
  brand: Brand;
  radio: RadioLines;
  song: Song;
  station: Station;
  character: Character;
  media: Media;
  help: HelpPage;
  playlist: Playlist;
  achievement: Achievement;
  walker: WalkerKind;
  mission: Mission;
}
export type Kind = keyof ContentKinds;

/** kind → directory under content/ */
export const KIND_DIRS: Record<Kind, string> = {
  terrain: 'terrains',
  road: 'roads',
  building: 'buildings',
  weapon: 'weapons',
  vehicle: 'vehicles',
  biome: 'biomes',
  zone: 'zones',
  place: 'places',
  prop: 'props',
  namelist: 'names',
  item: 'items',
  defence: 'defences',
  job: 'jobs',
  gang: 'gangs',
  economy: 'economy',
  ad: 'ads',
  cosmetic: 'cosmetics',
  store: 'stores',
  animal: 'animals',
  chatter: 'chatter',
  brand: 'brands',
  radio: 'radio',
  song: 'songs',
  station: 'stations',
  character: 'characters',
  help: 'help',
  media: 'media',
  playlist: 'playlists',
  achievement: 'achievements',
  walker: 'walkers',
  mission: 'missions',
};
export const KINDS = Object.keys(KIND_DIRS) as Kind[];

export const STORY_HEIGHT = 3;
export const AIRCRAFT_CLASSES: ReadonlySet<VehicleClass> = new Set(['plane', 'helicopter', 'drone']);
export const WATER_CLASSES: ReadonlySet<VehicleClass> = new Set(['boat']);
/** Minutes of cruising on a full tank, by class, unless stats.range says otherwise. */
export function vehicleRange(v: Vehicle): number {
  if (v.stats.range) return v.stats.range;
  switch (v.class) { case 'truck': case 'bus': return 40; case 'bike': return 15; case 'plane': case 'helicopter': return 30; case 'boat': return 30; case 'tank': return 25; default: return 25; }
}
export function vehiclePower(v: Vehicle): 'gas' | 'electric' { return v.power ?? (v.tags.includes('electric') || v.tags.includes('ev') ? 'electric' : 'gas'); }

export function buildingHeight(b: Building): number {
  return b.stories * (b.storyHeight ?? STORY_HEIGHT);
}

export const ECONOMY_DEFAULTS: Economy = {
  id: 'default', name: 'Default economy',
  robbery: { store: [40, 160], storeBig: 3, bank: [800, 2500], seconds: 8, bankSeconds: 12, armedKeeperChance: 0.3 },
  business: { perTile: 8, priceDays: 12, richMul: 1.6, zone: { downtown: 3, suburb: 1.2, industrial: 0.9, park: 1.4, rural: 0.7 }, extortChance: 0.15, extortShare: 0.4 },
  ticket: { base: 20, perTile: 0.05 },
  law: { payoffPerStar: 250, fineShare: 0.2, fineCapPerStar: 300, bountyPerStar: 100 },
  tuning: { engine: [0.25, 0.35, 0.5], armour: [0.2, 0.3], engineGain: 0.15, armourGain: 0.5, repair: 0.15 },
  loot: { zone: { downtown: 40, suburb: 22, industrial: 18, park: 15, rural: 10 } },
  turf: { tribute: 12, retakeDays: 4, retakeChance: 0.3, graceDays: 2 },
  karma: { priceSpread: 0.1, heatSpread: 0.3, animalAt: 30 },
  deathDrop: 0.25,
  fuel: { tank: 60, charge: 20 },
  tags: ['economy'],
};
